import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { useChangeIdentityPassword } from '@/gen/publicApi'

import { Button } from '@/components/button'
import { Form } from '@/components/form'
import FormInput from '@/components/form/formInput'
import { useToast } from '@/components/toast/useToast'

import { mapProblemDetailErrors, passwordSchema } from '@/utils/validation'

const passwordFormSchema = z.intersection(
    passwordSchema,
    z.object({
        currentPassword: z.string().min(1, 'Required'),
    }),
)

type PasswordFormValues = z.infer<typeof passwordFormSchema>

export default function ChangePassword() {
    const { toast } = useToast()
    const changePasswordMutator = useChangeIdentityPassword()
    const form = useForm<PasswordFormValues>({
        resolver: zodResolver(passwordFormSchema),
        defaultValues: {
            currentPassword: '',
            password: '',
            passwordConfirm: '',
        },
        mode: 'onBlur',
    })

    const onSubmit = (values: PasswordFormValues) => {
        changePasswordMutator.mutate(
            {
                currentPassword: values.currentPassword,
                newPassword: values.password,
                newPasswordConfirm: values.passwordConfirm,
            },
            {
                onSuccess: () => {
                    form.reset()
                    toast({
                        title: 'Password changed',
                    })
                },
                // @ts-expect-error - Kubb generated code is not typed correctly
                onError: (error: AxiosError) => {
                    mapProblemDetailErrors(error, form.formState.errors)
                },
            },
        )
    }

    return (
        <div className="flex items-center">
            <div className="self-start w-16 m-2" />
            <div className="flex-grow space-y-4">
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                        <FormInput
                            control={form.control}
                            name="currentPassword"
                            type="password"
                            label="Current password"
                        />
                        <FormInput control={form.control} name="password" type="password" label="New password" />
                        <FormInput
                            control={form.control}
                            name="passwordConfirm"
                            type="password"
                            label="Confirm new password"
                        />
                        <Button type="submit">Change</Button>
                    </form>
                </Form>
            </div>
        </div>
    )
}
